import React from 'react';
import {
	FormControl, FormControlLabel, MenuItem, Button,
	Select, RadioGroup, Stack, Box, Checkbox, Typography,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import { LocalizationProvider, DesktopDatePicker, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation, Trans } from 'react-i18next';
import { LANGUAGES } from './portalConfigs';

const formatOptionLabel = (value) => value.split('|').map((part) => part.trim()).join(', ');

const DateFilters = ({ classes, isMobile, filterValues, setFilterValues }) => {
	const { t, i18n } = useTranslation();
	const DatePicker = isMobile ? MobileDatePicker : DesktopDatePicker;

	return (
		<Box sx={classes.datesContainer}>
			{[['start_date', 'filters.startDate'], ['end_date', 'filters.endDate']].map(([field, labelKey]) => (
				<Box key={field} sx={classes.filterDateItem}>
					<Typography sx={classes.filterLabel}>{t(labelKey)}</Typography>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
						<DatePicker
							inputFormat="YYYY-MM-DD"
							value={filterValues[field]}
							onChange={(date) => setFilterValues({ ...filterValues, [field]: date })}
							sx={classes.datePicker}
						/>
					</LocalizationProvider>
				</Box>
			))}
		</Box>
	);
};

const SelectFilter = ({ classes, isMobile, label, value, options, onChange, selectSx }) => (
	<Box sx={classes.filterItem}>
		<Typography sx={classes.filterLabel}>{label}</Typography>
		<FormControl sx={classes.select}>
			<Select
				onChange={(e) => onChange(e.target.value)}
				// A list default (e.g. FEAST locations) shows as empty; the API still gets the list.
				value={Array.isArray(value) ? '' : value}
				sx={selectSx}
				style={{ '&:hover': { display: 'none'} } }
			>
				{options.map((option) => (
					<MenuItem
						key={option.value}
						value={option.value}
						sx={isMobile
							? { fontFamily: 'TT-Interphases-Regular' }
							: { fontFamily: 'TT-Interphases-Regular', fontSize: '20px' }}
					>
						{option.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	</Box>
);

const CheckboxFilter = ({
	classes, isMobile, label, filterKey, options, filterValues, setFilterValues,
}) => {
	const toggleOption = (value) => () => {
		setFilterValues((prev) => ({
			...prev,
			[filterKey]: prev[filterKey].includes(value)
				? prev[filterKey].filter((selected) => selected !== value)
				: [...prev[filterKey], value],
		}));
	};

	return (
		<Box sx={classes.filterCheckboxContainer}>
			<Typography sx={classes.filterLabel}>{label}</Typography>
			<FormControl component="fieldset">
				<RadioGroup sx={classes.checkboxGroup} style={{ '--options-count': options.length }}>
					{options.map((option) => (
						<FormControlLabel
							key={option.value}
							sx={isMobile ? classes.mobileCheckboxOption : classes.checkboxOption}
							control={
								<Checkbox
									checked={filterValues[filterKey].includes(option.value)}
									onChange={toggleOption(option.value)}
								/>
							}
							label={option.label}
						/>
					))}
				</RadioGroup>
			</FormControl>
		</Box>
	);
};

const SearchButton = ({ classes, isMobile, onSearch }) => {
	const { t } = useTranslation();
	return (
		<Box sx={classes.filterButton}>
			<Button variant="contained" color="primary" sx={classes.button} onClick={onSearch}>
				{isMobile ? <Search style={{cursor: 'pointer'}} /> : t('filters.search')}
			</Button>
		</Box>
	);
};

const ClearFilters = ({ classes, onClear }) => {
	const { t } = useTranslation();
	return <Box sx={classes.clearFilters} onClick={onClear}>{t('filters.clear')}</Box>;
};

const HomeDescription = ({ classes }) => {
	const { t } = useTranslation();
	return (
		<Box sx={classes.filterContainerTitle}>
			<Typography sx={classes.filterTitle}>{t('descriptionBold')}</Typography>
			<Typography sx={classes.filterDescription}>{t('description')}</Typography>
		</Box>
	);
};

const DedicatedSubtitle = ({ config, classes }) => {
	const { t } = useTranslation();
	return (
		<Typography sx={classes.filterDescription}>
			<Trans
				t={t}
				i18nKey={config.texts.subtitle}
				components={{
					1: (
						// eslint-disable-next-line jsx-a11y/anchor-has-content
						<a href={config.subtitleLink} target="_blank" rel="noopener noreferrer" />
					),
				}}
			/>
		</Typography>
	);
};

const PortalFilters = ({
	config, classes, isMobile, title, filterValues, setFilterValues, onSearch, onClear,
}) => {
	const { t } = useTranslation();
	const isHome = config.layout === 'home';
	const fieldProps = { classes, isMobile, filterValues, setFilterValues };
	const languages = config.languages || LANGUAGES;

	const eventTypeFilter = isHome && (
		<SelectFilter
			classes={classes}
			isMobile={isMobile}
			label={t('filters.eventType')}
			value={isMobile ? filterValues.event_type || '' : filterValues.event_type}
			options={['', ...config.eventTypeOptions].map((value) => ({
				value,
				label: t(`eventTypeTitles.${value}`),
			}))}
			onChange={(value) => setFilterValues({ ...filterValues, event_type: value })}
			selectSx={isMobile ? undefined : { fontSize: '20px' }}
		/>
	);

	const locationFilter = config.locations && (config.multipleLocations ? (
		<CheckboxFilter
			{...fieldProps}
			key="location"
			label={t('filters.location')}
			filterKey="location"
			options={config.locations}
		/>
	) : (
		<SelectFilter
			key="location"
			classes={classes}
			isMobile={isMobile}
			label={t('filters.location')}
			value={filterValues.location}
			options={config.locations}
			onChange={(value) => setFilterValues({ ...filterValues, location: value })}
		/>
	));

	const languageFilter = (
		<CheckboxFilter
			{...fieldProps}
			label={t('filters.languageLabel')}
			filterKey="language"
			options={languages.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
		/>
	);

	const extraFilters = (config.extraFilters || []).map(({ filterKey, labelKey, options }) => (
		<SelectFilter
			key={filterKey}
			classes={classes}
			isMobile={isMobile}
			label={t(labelKey)}
			value={filterValues[filterKey]}
			options={['', ...options].map((value) => ({ value, label: formatOptionLabel(value) }))}
			onChange={(value) => setFilterValues({ ...filterValues, [filterKey]: value })}
		/>
	));

	// Extra filters and location (if the portal has it), in display order.
	const selectFilters = [...extraFilters, locationFilter].filter(Boolean);

	if (isMobile) {
		return (
			<Box sx={classes.header}>
				<Stack direction="column" justifyContent="center" spacing={1}>
					<Typography sx={classes.title}>{title}</Typography>
					{isHome ? <HomeDescription classes={classes} /> : (
						<Box sx={classes.filterContainerTitle}>
							<DedicatedSubtitle config={config} classes={classes} />
							<Typography sx={classes.filterDescriptionTitle}>{t(config.texts.filterTitle)}</Typography>
						</Box>
					)}
					{eventTypeFilter}
					<DateFilters {...fieldProps} />
					{selectFilters}
					{languageFilter}
					<Stack direction="row" sx={classes.mobileButtonsContainer}>
						<Box sx={classes.filterButton}>
							<ClearFilters classes={classes} onClear={onClear} />
						</Box>
						<SearchButton classes={classes} isMobile onSearch={onSearch} />
					</Stack>
				</Stack>
			</Box>
		);
	}

	if (isHome) {
		return (
			<Box sx={classes.header}>
				<Stack direction="row" justifyContent={title !== '' ? 'space-between' : 'right'}>
					{title !== '' && <Typography sx={classes.title}>{title}</Typography>}
					<ClearFilters classes={classes} onClear={onClear} />
				</Stack>
				<Stack direction="column" spacing={2}>
					<Stack direction="row">
						<HomeDescription classes={classes} />
						{eventTypeFilter}
					</Stack>
					<Stack direction="row">
						<DateFilters {...fieldProps} />
						{locationFilter}
					</Stack>
					<Stack direction="row">
						{languageFilter}
						<SearchButton classes={classes} onSearch={onSearch} />
					</Stack>
				</Stack>
			</Box>
		);
	}

	return (
		<Box sx={classes.header}>
			<Stack direction="column" justifyContent="left" sx={{ marginBottom: '16px' }}>
				<Stack sx={classes.filterContainerTitle} direction="row">
					<Typography sx={classes.title}>{title}</Typography>
					<ClearFilters classes={classes} onClear={onClear} />
				</Stack>
				<DedicatedSubtitle config={config} classes={classes} />
				<Typography sx={classes.filterDescriptionTitle}>{t(config.texts.filterTitle)}</Typography>
			</Stack>
			<Stack direction="column" spacing={2}>
				<Stack direction="row" spacing={3}>
					<DateFilters {...fieldProps} />
					{selectFilters[0]}
				</Stack>
				{selectFilters.length > 1 && (
					<Stack direction="row" spacing={3}>
						{selectFilters.slice(1)}
						{config.multipleLocations && languageFilter}
					</Stack>
				)}
				<Stack direction="row" justifyContent="flex-end">
					{!config.multipleLocations && languageFilter}
					<SearchButton classes={classes} onSearch={onSearch} />
				</Stack>
			</Stack>
		</Box>
	);
};

export default PortalFilters;
